import { debug, warn } from '@4players/odin-common';
import { Channel as TauriChannel } from '@tauri-apps/api/core';

import {
  clearCanvasGlState,
  decodeRawPreviewFrame,
  makeSharedRingState,
  paintBgraGl,
  paintNv12,
  PerfBucket,
  pollSharedRing,
  RawFrameFormat,
  SHARED_MAGIC,
  verifySharedSlot,
  type RawPreviewFrame,
  type SharedFrameBufferMeta,
  type SharedRingState,
} from '../raw-frame';
import { renderMode, renderWorker } from '../render/client';

// Re-exports so existing importers keep their single import site —
// the definitions moved to `raw-frame.ts` when the render worker
// started sharing them.
export { decodeRawPreviewFrame, RawFrameFormat } from '../raw-frame';
export type { RawPreviewFrame, SharedFrameBufferMeta } from '../raw-frame';

/** ── WebView2 shared frame buffers (Windows zero-IPC-copy path) ──
 *
 *  Mirror of `src-tauri/plugins/odin/src/video/shared_frame_buffer.rs`: Rust maps
 *  one buffer into both processes, writes frames into a 3-slot ring
 *  guarded by a seqlock, and posts the read-only `ArrayBuffer` here
 *  via `chrome.webview.postSharedBufferToScript`. The receiver then
 *  polls the ring once per rAF instead of parsing per-frame IPC
 *  messages — the pixel bytes never cross the process boundary as a
 *  copy again. The Tauri channel stays attached in parallel as the
 *  fallback for oversize frames and pre-shared-buffer runtimes. */

interface SharedBufferDelivery {
  buffer: ArrayBuffer;
  meta: SharedFrameBufferMeta;
}

interface WebView2SharedBufferEvent {
  getBuffer(): ArrayBuffer;
  additionalData?: unknown;
}
interface ChromeWebView {
  addEventListener(type: 'sharedbufferreceived', cb: (e: WebView2SharedBufferEvent) => void): void;
  releaseBuffer?(buffer: ArrayBuffer): void;
}

function chromeWebView(): ChromeWebView | null {
  return (window as unknown as { chrome?: { webview?: ChromeWebView } }).chrome?.webview ?? null;
}

/** True in WebView2 — the only engine with the shared-buffer API. */
export function sharedFrameBuffersSupported(): boolean {
  return chromeWebView() !== null;
}

export function makeSharedFrameBufferToken(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Buffers can land before or after the consumer registers (the post
 *  races the invoke's resolution), so both sides go through this
 *  token-keyed rendezvous. Entries on either side expire after
 *  `SHARED_RENDEZVOUS_TTL_MS`: an unclaimed delivery pins multi-MB of
 *  shared memory and an unserved waiter pins its receiver — both
 *  happen when the other side failed (invoke error, runtime without
 *  shared buffers) and would otherwise accumulate forever. */
const sharedBufferWaiters = new Map<string, (d: SharedBufferDelivery) => void>();
const pendingSharedBuffers = new Map<string, SharedBufferDelivery>();
let sharedBufferListenerInstalled = false;
const SHARED_RENDEZVOUS_TTL_MS = 30_000;

function releaseSharedBuffer(buffer: ArrayBuffer): void {
  chromeWebView()?.releaseBuffer?.(buffer);
}

function installSharedBufferListener(): void {
  if (sharedBufferListenerInstalled) return;
  const wv = chromeWebView();
  if (!wv) return;
  sharedBufferListenerInstalled = true;
  wv.addEventListener('sharedbufferreceived', (e) => {
    // `additionalData` arrives pre-parsed on current runtimes; accept
    // a JSON string too so a runtime quirk degrades to "ignored"
    // instead of an exception in a global handler.
    let data = e.additionalData;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return;
      }
    }
    const meta = data as (SharedFrameBufferMeta & { odinSharedFrameBuffer?: boolean }) | null;
    if (!meta || meta.odinSharedFrameBuffer !== true || typeof meta.token !== 'string') return;
    const delivery: SharedBufferDelivery = { buffer: e.getBuffer(), meta };
    const waiter = sharedBufferWaiters.get(meta.token);
    if (waiter) {
      sharedBufferWaiters.delete(meta.token);
      waiter(delivery);
      return;
    }
    pendingSharedBuffers.set(meta.token, delivery);
    setTimeout(() => {
      const stale = pendingSharedBuffers.get(meta.token);
      if (stale !== delivery) return;
      pendingSharedBuffers.delete(meta.token);
      releaseSharedBuffer(stale.buffer);
      debug(`[raw-preview] released unclaimed shared buffer (token=${meta.token})`);
    }, SHARED_RENDEZVOUS_TTL_MS);
  });
}

/** TEMPORARY one-shot capability probe: can the WebView2 shared-buffer
 *  ArrayBuffer be TRANSFERRED into a Web Worker? Answered TRANSFERABLE
 *  on 2026-07-13 (magic ok, full ring readable in the worker) — the
 *  render worker builds on that. Kept for one more release as a
 *  telemetry line across the fleet's runtime spread; runs at detach
 *  time on a buffer we were about to release anyway, and only on the
 *  main-thread path (in worker mode the buffer lives in the worker).
 *  Logs at WARN so release builds show the verdict without debug
 *  verbosity. */
let sharedTransferCheckDone = false;

function runSharedBufferTransferCheck(buffer: ArrayBuffer): void {
  sharedTransferCheckDone = true;
  const workerSrc = `onmessage = (e) => {
    try {
      const dv = new DataView(e.data);
      postMessage({ ok: true, byteLength: e.data.byteLength, magic: dv.getUint32(0, true) });
    } catch (err) {
      postMessage({ ok: false, err: String(err) });
    }
  };`;
  const url = URL.createObjectURL(new Blob([workerSrc], { type: 'text/javascript' }));
  let worker: Worker;
  try {
    worker = new Worker(url);
  } catch (err) {
    URL.revokeObjectURL(url);
    // `warn` (not console.warn): only the odin-common loggers cross
    // the [UI] bridge into the Rust log file, and this verdict must
    // be visible in release logs.
    warn(`[shm-frames] worker transfer check: worker spawn failed: ${err}`);
    releaseSharedBuffer(buffer);
    return;
  }
  const done = (msg: string) => {
    warn(`[shm-frames] worker transfer check: ${msg}`);
    worker.terminate();
    URL.revokeObjectURL(url);
  };
  const timeout = setTimeout(() => done('no worker response after 3s'), 3000);
  worker.onmessage = (e) => {
    clearTimeout(timeout);
    const d = e.data as { ok: boolean; byteLength?: number; magic?: number; err?: string };
    if (!d.ok) {
      done(`TRANSFERABLE but worker read failed: ${d.err}`);
      return;
    }
    const magicOk = d.magic === SHARED_MAGIC;
    done(
      `TRANSFERABLE — worker read ${d.byteLength} bytes, magic ${magicOk ? 'ok' : 'MISMATCH (buffer already closed?)'}`,
    );
  };
  worker.onerror = (e) => {
    clearTimeout(timeout);
    done(`worker error: ${e.message}`);
  };
  try {
    worker.postMessage(buffer, [buffer]);
    if (buffer.byteLength !== 0) {
      // The transfer list was accepted but the main-thread view did
      // not detach — the runtime cloned instead of moving. Counts as
      // "not transferable" for the worker-renderer design (a clone
      // would double the memory and lose the shared mapping).
      clearTimeout(timeout);
      done('buffer was CLONED, not transferred');
      releaseSharedBuffer(buffer);
    }
  } catch (err) {
    clearTimeout(timeout);
    done(`NOT transferable: ${err}`);
    releaseSharedBuffer(buffer);
  }
}

/** Route the shared buffer posted under `token` to `cb` — immediately
 *  when it already arrived, otherwise on delivery. Returns a dispose
 *  fn; call it when the buffer is no longer wanted (invoke failed,
 *  subscription replaced) so a late delivery gets released instead of
 *  attached. Waiters also self-expire (see the rendezvous docs). */
export function awaitSharedFrameBuffer(
  token: string,
  cb: (buffer: ArrayBuffer, meta: SharedFrameBufferMeta) => void,
): () => void {
  installSharedBufferListener();
  const pending = pendingSharedBuffers.get(token);
  if (pending) {
    pendingSharedBuffers.delete(token);
    cb(pending.buffer, pending.meta);
    return () => {};
  }
  const waiter = (d: SharedBufferDelivery) => cb(d.buffer, d.meta);
  sharedBufferWaiters.set(token, waiter);
  const dispose = () => {
    if (sharedBufferWaiters.get(token) === waiter) {
      sharedBufferWaiters.delete(token);
    }
  };
  setTimeout(dispose, SHARED_RENDEZVOUS_TTL_MS);
  return dispose;
}

/** True when painting this canvas produces visible pixels. Detached
 *  canvases and anything inside a `display: none` /
 *  `visibility: hidden` subtree still cost a full GPU upload (NV12)
 *  or CPU swizzle (BGRA) per frame if painted — `checkVisibility`
 *  reads the computed style without forcing layout in steady state.
 *  Engines without `checkVisibility` (older WebKitGTK) paint
 *  unconditionally, same as before. */
function isPaintable(canvas: HTMLCanvasElement): boolean {
  if (!canvas.isConnected) return false;
  return canvas.checkVisibility?.({ checkVisibilityCSS: true, visibilityProperty: true }) ?? true;
}

/** Chromium/WebView2 exposes `performance.memory` for the JS heap.
 *  Not standard so we have to widen the type ourselves. Returns 0 on
 *  engines that lack the field (anything non-Chromium). */
function jsHeapMb(): number {
  const mem = (performance as unknown as { memory?: { usedJSHeapSize?: number } }).memory;
  const used = mem?.usedJSHeapSize ?? 0;
  return used / (1024 * 1024);
}

/** Local self-preview pipeline that bypasses encode + WebCodecs
 *  decode entirely. Frames arrive as raw BGRA (publisher-side
 *  capture) or NV12 (receiver-side native H.264 decoder) from Rust
 *  and this class paints them straight into every mounted `<canvas>`.
 *
 *  Why this exists: the WebCodecs H.264 decode pipeline carries
 *  multi-hundred-ms latency on WebKit and a JS-thread cost that
 *  starves the audio worklet on weaker boxes (VMs in particular).
 *  Bypassing it for both the publisher's own preview tile and the
 *  receiver-side decoded canvas avoids both problems.
 *
 *  Where painting happens is decided once per app run by
 *  `renderMode()` (see `render-worker-client.ts`):
 *
 *    - `'worker'`: canvases are transferred to the render worker,
 *      which polls shared-buffer rings itself and receives channel
 *      frames as ownership-moved ArrayBuffers — texture uploads and
 *      draws never touch the main thread.
 *    - `'main'`: the historical in-place path — WebGL painting right
 *      here, rAF-gated. Serves engines without OffscreenCanvas WebGL
 *      or worker rAF (older WebKitGTK / WKWebView).
 *
 *  Until the mode resolves (one worker round-trip) no canvas is
 *  painted — a transferred canvas can never be painted from the main
 *  thread again, so the decision must precede the first
 *  `getContext` call.
 *
 *  Paint path picked per-frame from `frame.format`:
 *    - `Bgra` → WebGL fragment shader swaps the channels on the GPU
 *               (2D canvas + CPU swizzle + `putImageData` fallback
 *               where WebGL is unavailable, main mode only)
 *    - `Nv12` → WebGL fragment shader does YUV→RGB on the GPU, no
 *               CPU pixel work
 *
 *  API surface deliberately matches `NativeVideoReceiver`'s `mountTo`
 *  + `stream` so the host stream-view wrapper component can swap
 *  receivers without template changes. */
export class NativeRawVideoReceiver {
  private closed = false;
  /** Most recently received frame. Kept so a canvas mounted mid-
   *  stream gets repainted with current content instead of staying
   *  blank until the next frame ticks. (Main mode only — the worker
   *  keeps its own latest frame per stream.) */
  private latestFrame: RawPreviewFrame | null = null;
  private readonly targets = new Set<HTMLCanvasElement>();
  /** Stub MediaStream that exists only to carry the `__odinNative*`
   *  side-channel tags the rest of the pipeline expects (the ODIN
   *  tauri-plugin reads `__odinNativeHandle` here to attach the
   *  WebRTC sender to the same Rust-side stream). Has no media tracks
   *  — `<video>` would render it blank, which is why we mount onto
   *  the canvas directly. */
  readonly stream: MediaStream;
  /** Persistent RGBA scratch buffer for the BGRA→RGBA swizzle on the
   *  Bgra paint path's CPU fallback (WebGL unavailable on the
   *  canvas). At 1080p BGRA each frame is 8 MB; allocating a
   *  fresh `Uint8ClampedArray` per paint would burn ~120 MB/s of GC
   *  throughput at 15 fps and trigger periodic compositor stalls.
   *  Generic pinned to `ArrayBuffer` (rather than the default
   *  `ArrayBufferLike`) so the type matches `ImageData`'s constructor
   *  signature on modern TS lib defs (it rejects `SharedArrayBuffer`-
   *  backed typed arrays). */
  private rgbaScratch: Uint8ClampedArray<ArrayBuffer> | null = null;

  /** Render-mode state machine. `'pending'` until the one-shot
   *  `renderMode()` probe resolves; every paint-adjacent action is
   *  deferred or routed based on this. */
  private mode: 'pending' | 'worker' | 'main' = 'pending';
  /** Worker-side stream id, allocated when the mode resolves to
   *  `'worker'`. */
  private sid: number | null = null;
  /** Worker canvas ids per mounted canvas (worker mode only). */
  private readonly workerCanvasIds = new Map<HTMLCanvasElement, number>();
  /** Visibility hints for worker-owned canvases — the worker can't
   *  run `checkVisibility`, so the main thread observes and reports. */
  private intersection: IntersectionObserver | null = null;
  /** Shared buffer that arrived while the mode was still pending. */
  private pendingShm: SharedBufferDelivery | null = null;
  /** Canvases already warned about a WebGL init failure — the paint
   *  helpers latch the failure per canvas, so without this set the
   *  caller would warn once per frame. */
  private readonly glWarned = new WeakSet<HTMLCanvasElement>();

  /** Wall-clock-bucketed receive + paint counters, logged once per
   *  second. `pushCount` ticks every frame that lands in `push()`
   *  (= every frame Tauri delivered to JS). `paintCount` ticks
   *  every successful `paintNv12`/`paintBgra` call (= every frame
   *  the canvas actually rendered, after the rAF gate coalesces
   *  bursts to one paint per display refresh). The gap between the
   *  two is the implicit-frame-drop count when arrival rate exceeds
   *  display refresh. Paired with `[dxva-decoder] emit rate` on the
   *  Rust side for end-to-end accounting. In worker mode the worker
   *  posts an equivalent per-second line instead. */
  private rateWindowStarted = performance.now();
  private pushCount = 0;
  private paintCount = 0;
  private lastFrameBytes = 0;
  private lastFrameDims = '';

  /** Pending `requestAnimationFrame` handle, or `null` when no render
   *  is queued. Painting is rAF-driven so that ingest rate (which can
   *  exceed display refresh — DXVA emits 60 fps over a 60 Hz display
   *  with bursts up to 120 fps on recovery) doesn't saturate the main
   *  thread with `texSubImage2D` work the compositor never gets to
   *  flip. Multiple pushes between two rAF ticks coalesce to one paint
   *  of the latest frame; older frames are dropped on the floor on
   *  purpose. */
  private rafHandle: number | null = null;

  /** Wall clock of the last `push()` entry and the last
   *  `renderLatest()` fire. Differences across these are recorded in
   *  the matching buckets below so we can spot e.g. "Tauri delivered
   *  20 push callbacks back-to-back" (bursty IPC) vs steady cadence.
   *  `rafGapBucket` shows whether `requestAnimationFrame` itself is
   *  firing on a 16 ms display cadence or getting throttled by main-
   *  thread saturation. */
  private lastPushAt = 0;
  private lastRenderAt = 0;
  private pushGapBucket = new PerfBucket();
  private rafGapBucket = new PerfBucket();
  private paintBucket = new PerfBucket();

  /** Fired from `attach()`'s onmessage as soon as a frame is pulled
   *  off the Tauri channel (NOT for shared-buffer frames — those have
   *  no in-flight accounting). Remote tracks and publisher-side
   *  previews wire this to their matching fire-and-forget consumed
   *  command so Rust can reopen one producer-side channel slot. */
  private framePulledCallback: (() => void) | null = null;

  /** Fired on every 0↔1 transition of the mounted-canvas count.
   *  The native capture API wires this to `stream_subscribe_preview` /
   *  `stream_unsubscribe_preview` so the Rust capture path only
   *  produces preview frames while someone is actually watching —
   *  an unwatched preview still costs the full GPU readback + IPC +
   *  paint pipeline (~12 percentage points of CPU on a 1440p60
   *  Windows screen-share). */
  private mountStateCallback: ((mounted: boolean) => void) | null = null;

  /** Fired whenever the largest displayed width (in device pixels)
   *  across all mounted canvases changes. The ODIN tauri-plugin maps
   *  this to `webrtc_video_remote_track_set_max_fps` so a stream
   *  that's only visible as a small grid tile doesn't pay the full
   *  post-decode readback + IPC + paint pipeline at 60 fps, while a
   *  fullscreen / large tile lifts the cap again. */
  private displaySizeCallback: ((maxWidthDevicePx: number) => void) | null = null;
  private lastReportedDisplayWidth = -1;
  /** Lazily-created observer over the mounted canvases, driving
   *  `recomputeDisplaySize` on layout changes. */
  private resizeObserver: ResizeObserver | null = null;

  constructor() {
    this.stream = new MediaStream();
    void renderMode().then((m) => this.onModeResolved(m));
  }

  private onModeResolved(m: 'worker' | 'main'): void {
    if (this.closed) return;
    if (m === 'worker') {
      const client = renderWorker();
      if (!client) {
        // Defensive: mode says worker but the client is gone —
        // behave like main so painting still happens.
        this.mode = 'main';
        return;
      }
      this.mode = 'worker';
      this.sid = client.allocStream();
      for (const canvas of this.targets) {
        this.mountToWorker(canvas);
      }
      if (this.pendingShm) {
        const p = this.pendingShm;
        this.pendingShm = null;
        client.attachSharedBuffer(this.sid, p.buffer, p.meta);
        debug(`[raw-preview] shared frame buffer forwarded to worker (token=${p.meta.token})`);
      }
      return;
    }
    this.mode = 'main';
    if (this.pendingShm) {
      const p = this.pendingShm;
      this.pendingShm = null;
      this.attachSharedBuffer(p.buffer, p.meta);
    }
    if (this.latestFrame && this.targets.size > 0) {
      this.scheduleRender();
    }
  }

  /** True while at least one canvas is mounted. */
  get isMounted(): boolean {
    return this.targets.size > 0;
  }

  /** Wire the mount-state hook (see `mountStateCallback`). Set once
   *  right after construction, before any canvas can mount. */
  setMountStateCallback(cb: (mounted: boolean) => void): void {
    this.mountStateCallback = cb;
  }

  /** Wire the display-size hook (see `displaySizeCallback`) and
   *  report the current size right away when canvases are already
   *  mounted. */
  setDisplaySizeCallback(cb: (maxWidthDevicePx: number) => void): void {
    this.displaySizeCallback = cb;
    this.lastReportedDisplayWidth = -1;
    this.recomputeDisplaySize();
  }

  private ensureResizeObserver(): ResizeObserver | null {
    if (this.resizeObserver) return this.resizeObserver;
    if (typeof ResizeObserver === 'undefined') return null;
    this.resizeObserver = new ResizeObserver(() => this.recomputeDisplaySize());
    return this.resizeObserver;
  }

  private recomputeDisplaySize(): void {
    if (!this.displaySizeCallback) return;
    let max = 0;
    for (const canvas of this.targets) {
      const w = canvas.getBoundingClientRect().width * (window.devicePixelRatio || 1);
      if (w > max) max = w;
    }
    const rounded = Math.round(max);
    if (rounded === this.lastReportedDisplayWidth) return;
    this.lastReportedDisplayWidth = rounded;
    this.displaySizeCallback(rounded);
  }

  private ensureIntersectionObserver(): IntersectionObserver | null {
    if (this.intersection) return this.intersection;
    if (typeof IntersectionObserver === 'undefined') return null;
    this.intersection = new IntersectionObserver((entries) => {
      const client = renderWorker();
      if (!client) return;
      for (const entry of entries) {
        const cid = this.workerCanvasIds.get(entry.target as HTMLCanvasElement);
        if (cid !== undefined) {
          client.setCanvasVisible(cid, entry.isIntersecting);
        }
      }
    });
    return this.intersection;
  }

  /** Wire a Tauri Channel<ArrayBuffer> (from
   *  `stream_subscribe_preview`) to this receiver. */
  attach(channel: TauriChannel<ArrayBuffer>): void {
    channel.onmessage = (buf) => {
      // Ack channel-delivered frames only — shared-buffer frames have
      // no Rust-side in-flight accounting to release.
      this.framePulledCallback?.();
      if (this.mode === 'worker' && this.sid !== null) {
        // Ownership-move into the render worker (no copy); decode
        // and paint happen there.
        renderWorker()?.pushFrame(this.sid, buf);
        return;
      }
      this.push(decodeRawPreviewFrame(buf));
    };
  }

  /** Main-thread shared-buffer ring, or `null` while frames ride the
   *  channel / the worker owns the ring. Replaced wholesale on
   *  re-attach (each Rust-side subscribe posts a fresh buffer). */
  private shared: SharedRingState | null = null;
  private sharedPollHandle: number | null = null;

  /** Route a freshly delivered WebView2 shared buffer to whichever
   *  side paints: forwarded (transferred) to the render worker, or
   *  polled right here in main mode. While the render mode is still
   *  pending the delivery is parked. */
  attachSharedBuffer(buffer: ArrayBuffer, meta: SharedFrameBufferMeta): void {
    if (this.closed) {
      releaseSharedBuffer(buffer);
      return;
    }
    if (this.mode === 'pending') {
      if (this.pendingShm) releaseSharedBuffer(this.pendingShm.buffer);
      this.pendingShm = { buffer, meta };
      return;
    }
    if (this.mode === 'worker' && this.sid !== null) {
      renderWorker()?.attachSharedBuffer(this.sid, buffer, meta);
      debug(`[raw-preview] shared frame buffer forwarded to worker (token=${meta.token})`);
      return;
    }
    // Main mode. The header validation can throw when this is a LATE
    // delivery whose Rust-side writer is already gone (fast
    // subscribe→unsubscribe cycle closes the buffer before the post
    // reaches us) — `makeSharedRingState` guards that internally.
    const ring = makeSharedRingState(buffer, meta);
    if (!ring) {
      warn('[raw-preview] shared buffer header mismatch; staying on channel IPC');
      releaseSharedBuffer(buffer);
      return;
    }
    this.detachSharedBuffer();
    this.shared = ring;
    debug(`[raw-preview] shared frame buffer attached (token=${meta.token})`);
    this.startSharedPoll();
  }

  /** Drop the shared-buffer view. Called before the Rust side closes
   *  the buffer (preview unsubscribe) so the poll loop never touches
   *  unmapped memory, and defensively from the poll's catch. */
  detachSharedBuffer(): void {
    if (this.pendingShm) {
      releaseSharedBuffer(this.pendingShm.buffer);
      this.pendingShm = null;
    }
    if (this.mode === 'worker') {
      if (this.sid !== null) {
        renderWorker()?.detachSharedBuffer(this.sid);
      }
      return;
    }
    const shared = this.shared;
    this.shared = null;
    if (this.sharedPollHandle !== null) {
      cancelAnimationFrame(this.sharedPollHandle);
      this.sharedPollHandle = null;
    }
    if (shared) {
      // The cached frame may view into the buffer we're about to
      // release — drop it so a later mount doesn't repaint from
      // unmapped memory.
      this.latestFrame = null;
      if (!sharedTransferCheckDone) {
        // Feed the buffer to the one-shot worker-transfer probe
        // instead of releasing it — it's on its way out either way.
        runSharedBufferTransferCheck(shared.buffer);
      } else {
        releaseSharedBuffer(shared.buffer);
      }
    }
  }

  private startSharedPoll(): void {
    if (this.sharedPollHandle !== null) return;
    const tick = () => {
      if (this.closed || !this.shared) {
        this.sharedPollHandle = null;
        return;
      }
      try {
        this.pollShared();
      } catch (e) {
        // The Rust side closed the buffer under us (unsubscribe /
        // track teardown race) — detach and let the channel fallback
        // carry any remaining frames.
        debug(`[raw-preview] shared buffer detached after access error: ${e}`);
        this.detachSharedBuffer();
        return;
      }
      this.sharedPollHandle = requestAnimationFrame(tick);
    };
    this.sharedPollHandle = requestAnimationFrame(tick);
  }

  private pollShared(): void {
    const s = this.shared;
    if (!s) return;
    const frame = pollSharedRing(s);
    if (!frame) return;
    // Bookkeeping mirrors `push()`; painting runs inline because we
    // are already inside a rAF tick — scheduling another one would
    // only add a display frame of latency.
    const now = performance.now();
    if (this.lastPushAt !== 0) {
      this.pushGapBucket.record(now - this.lastPushAt);
    }
    this.lastPushAt = now;
    this.latestFrame = frame;
    this.pushCount += 1;
    this.lastFrameBytes = frame.data.byteLength;
    this.lastFrameDims = `${frame.width}x${frame.height}`;
    if (this.targets.size > 0) {
      this.renderLatest();
    }
    // Seqlock exit check: a climbing tear count means the writer
    // laps our paints — with three slots that takes two full frames
    // produced within one paint, so it signals real trouble.
    const before = s.tearCount;
    verifySharedSlot(s);
    if (s.tearCount !== before && s.tearCount % 60 === 1) {
      debug(`[raw-preview] shared buffer torn frames: ${s.tearCount}`);
    }
    this.logRateIfDue();
  }

  /** Wire the JS→Rust "frame consumed" acknowledgement for either a
   *  remote media id or a local native stream handle. */
  setFramePulledCallback(cb: () => void): void {
    this.framePulledCallback = cb;
  }

  push(frame: RawPreviewFrame): void {
    if (this.closed) return;
    const now = performance.now();
    // Inter-push gap shows the IPC delivery cadence. Steady ~16-17ms
    // = Tauri keeps up with the 60 fps producer; bursts of <1ms
    // followed by long gaps = messages are batched in the channel
    // queue and delivered in bunches (back-pressure from main thread).
    if (this.lastPushAt !== 0) {
      this.pushGapBucket.record(now - this.lastPushAt);
    }
    this.lastPushAt = now;
    this.latestFrame = frame;
    this.pushCount += 1;
    this.lastFrameBytes = frame.data.byteLength;
    this.lastFrameDims = `${frame.width}x${frame.height}`;
    // Schedule (or piggyback on an already-queued) rAF rather than
    // painting inline. At 55-60 fps arrival × 3-5 MB per frame the
    // inline `texSubImage2D` saturates the main thread to the point
    // where WebView2's compositor stops flipping the canvas — the
    // user perceives "1 frame every 10s + UI freeze" even though
    // every frame technically passed through paint.
    //
    // While the render mode is pending only `latestFrame` updates —
    // painting starts once the mode is decided (a canvas must not
    // get a main-thread GL context if it's about to be transferred).
    if (this.mode === 'main' && this.targets.size > 0) {
      this.scheduleRender();
    }
    this.logRateIfDue();
  }

  private scheduleRender(): void {
    if (this.rafHandle !== null) return;
    this.rafHandle = requestAnimationFrame(() => {
      this.rafHandle = null;
      this.renderLatest();
    });
  }

  private renderLatest(): void {
    if (this.closed) return;
    const frame = this.latestFrame;
    if (!frame) return;
    // rAF gap: time since the last rAF fire. On a 60 Hz display
    // this should sit at ~16.7ms; any larger means the browser
    // compositor is starved.
    const now = performance.now();
    if (this.lastRenderAt !== 0) {
      this.rafGapBucket.record(now - this.lastRenderAt);
    }
    this.lastRenderAt = now;
    // paint duration: every `paint()` call wraps a WebGL upload
    // (`texSubImage2D` for Y + UV planes) + a `drawArrays`. If the
    // duration spikes the GPU side of the compositor is contended.
    const paintStart = performance.now();
    for (const target of this.targets) {
      // Skip canvases the user can't currently see: detached nodes
      // and anything inside a `display: none` / `visibility: hidden`
      // subtree (mobile's hidden main grid, the grid veiled behind
      // the fullscreen overlay, background views). The canvas keeps
      // its last painted content, and the next frame after it turns
      // visible repaints it — mounting stays untouched, so the
      // Rust-side preview subscription keeps flowing for the
      // remaining visible canvases.
      if (!isPaintable(target)) continue;
      this.paint(target, frame);
      this.paintCount += 1;
    }
    this.paintBucket.record(performance.now() - paintStart);
  }

  private logRateIfDue(): void {
    const now = performance.now();
    const elapsed = now - this.rateWindowStarted;
    if (elapsed < 1000) return;
    const pushFps = (this.pushCount * 1000) / elapsed;
    const paintFps = (this.paintCount * 1000) / elapsed;
    const targets = this.targets.size;
    const dropFps = pushFps - paintFps;
    // Dump per-stage timings + drop count at DEBUG so production
    // logs (TauriLogSink default = WARN) stay quiet. Bring it back
    // by enabling Debug-level logging in the host. Format mirrors
    // the Rust-side `[dxva-decoder] perf rate=…` line so a `grep
    // '\[raw-preview\]\|\[dxva-decoder\]'` shows the two pipelines
    // side by side per second. All durations are ms; (avg/max).
    debug(
      `[raw-preview] perf push=${pushFps.toFixed(1)}fps ` +
        `paint=${paintFps.toFixed(1)}fps drop=${dropFps.toFixed(1)}fps ` +
        `targets=${targets} dims=${this.lastFrameDims} ` +
        `payload=${this.lastFrameBytes}B heap=${jsHeapMb().toFixed(0)}MB ` +
        `push_gap=${this.pushGapBucket.avgMs().toFixed(1)}/${this.pushGapBucket.maxMs.toFixed(1)}ms ` +
        `raf_gap=${this.rafGapBucket.avgMs().toFixed(1)}/${this.rafGapBucket.maxMs.toFixed(1)}ms ` +
        `paint_dur=${this.paintBucket.avgMs().toFixed(1)}/${this.paintBucket.maxMs.toFixed(1)}ms`,
    );
    this.pushCount = 0;
    this.paintCount = 0;
    this.rateWindowStarted = now;
    this.pushGapBucket.reset();
    this.rafGapBucket.reset();
    this.paintBucket.reset();
  }

  private mountToWorker(canvas: HTMLCanvasElement): void {
    if (this.sid === null) return;
    const client = renderWorker();
    if (!client) return;
    const cid = client.mountCanvas(this.sid, canvas);
    if (cid === null) return;
    this.workerCanvasIds.set(canvas, cid);
    this.ensureIntersectionObserver()?.observe(canvas);
  }

  /** Register a canvas as a paint target. Same semantics as
   *  `NativeVideoReceiver.mountTo` — returns an unmount function. */
  mountTo(canvas: HTMLCanvasElement): () => void {
    if (this.closed) return () => {};
    this.targets.add(canvas);
    this.ensureResizeObserver()?.observe(canvas);
    this.recomputeDisplaySize();
    if (this.targets.size === 1) {
      this.mountStateCallback?.(true);
    }
    if (this.mode === 'worker') {
      this.mountToWorker(canvas);
    } else if (this.mode === 'main' && this.latestFrame) {
      // Snap the cached frame onto the freshly-mounted canvas via
      // the same rAF gate the steady-state path uses — keeps all
      // paints on the compositor's cadence and avoids a synchronous
      // 3 MB upload during component mount.
      this.scheduleRender();
    }
    // Pending mode: painting starts once the render mode resolves
    // (`onModeResolved` walks the targets set).
    return () => {
      const removed = this.targets.delete(canvas);
      this.resizeObserver?.unobserve(canvas);
      this.recomputeDisplaySize();
      if (removed && this.targets.size === 0 && !this.closed) {
        this.mountStateCallback?.(false);
      }
      const cid = this.workerCanvasIds.get(canvas);
      if (cid !== undefined) {
        // Worker owns the backing (transfer is irreversible) — the
        // worker keeps the OffscreenCanvas parked for a potential
        // remount and drops it on stream close.
        this.workerCanvasIds.delete(canvas);
        this.intersection?.unobserve(canvas);
        renderWorker()?.unmountCanvas(cid);
        return;
      }
      // Drop the WebGL state so the GPU resources are released; the
      // browser GCs the canvas's WebGL context separately but
      // dropping our handle helps it happen sooner.
      clearCanvasGlState(canvas);
    };
  }

  private paint(canvas: HTMLCanvasElement, frame: RawPreviewFrame): void {
    if (frame.format === RawFrameFormat.Nv12) {
      if (!paintNv12(canvas, frame) && !this.glWarned.has(canvas)) {
        this.glWarned.add(canvas);
        warn('[raw-preview] NV12 WebGL init failed; cannot paint');
      }
      return;
    }
    this.paintBgra(canvas, frame);
  }

  private paintBgra(canvas: HTMLCanvasElement, frame: RawPreviewFrame): void {
    // GPU path first — the shader does the channel swap for free.
    if (paintBgraGl(canvas, frame)) return;
    if (!this.glWarned.has(canvas)) {
      this.glWarned.add(canvas);
      warn('[raw-preview] BGRA WebGL init failed; using CPU swizzle fallback');
    }

    // CPU fallback for canvases without WebGL (VM software GL
    // blocklists, context lost): per-pixel swizzle + putImageData.
    if (frame.width !== canvas.width || frame.height !== canvas.height) {
      canvas.width = frame.width;
      canvas.height = frame.height;
    }
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;
    // BGRA → RGBA swizzle into a persistent scratch buffer.
    // `putImageData` reads the data synchronously and copies into the
    // canvas backing, so we can safely overwrite the same buffer on
    // the next frame — avoids 8 MB allocs/frame at 1080p.
    const src = frame.data;
    if (!this.rgbaScratch || this.rgbaScratch.length !== src.length) {
      this.rgbaScratch = new Uint8ClampedArray(src.length);
    }
    const rgba = this.rgbaScratch;
    for (let i = 0; i < src.length; i += 4) {
      rgba[i] = src[i + 2]; // R ← B
      rgba[i + 1] = src[i + 1]; // G
      rgba[i + 2] = src[i]; // B ← R
      rgba[i + 3] = 255; // A — source alpha is meaningless from screencast (often 0)
    }
    const img = new ImageData(rgba, frame.width, frame.height);
    ctx.putImageData(img, 0, 0);
  }

  close(): void {
    if (this.closed) return;
    this.detachSharedBuffer();
    this.closed = true;
    if (this.sid !== null) {
      renderWorker()?.closeStream(this.sid);
      this.sid = null;
    }
    this.workerCanvasIds.clear();
    this.intersection?.disconnect();
    this.intersection = null;
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    this.latestFrame = null;
    for (const target of this.targets) {
      clearCanvasGlState(target);
    }
    this.targets.clear();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.rgbaScratch = null;
  }
}

/** Read the `NativeRawVideoReceiver` stashed on a MediaStream by
 *  the native capture API on the Tauri raw-preview path, or `null` for
 *  encoded streams / web getUserMedia. The host stream-view UI
 *  consults this (alongside `nativeReceiverOf`) to pick whether to
 *  mount via the raw receiver, the encoded receiver, or fall back to
 *  `<video [srcObject]>`. */
export function nativeRawReceiverOf(
  stream: MediaStream | null | undefined,
): NativeRawVideoReceiver | null {
  if (!stream) return null;
  return (
    (stream as unknown as { __odinNativeRawReceiver?: NativeRawVideoReceiver })
      .__odinNativeRawReceiver ?? null
  );
}
